import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styles from 'styles/admin-detail.module.scss';
import Alert from '@material-ui/lab/Alert';
import AsyncButton from 'utils/AsyncButton';
import { lockApply, unlockApply } from 'api/applies';
import { currentUserSelector } from "store/current_user";

interface Props {
  applyId: string,
  lockedBy: string | null,
  isBasicInformationInputChanged: boolean,
  setIsBasicInformationInputChanged: React.Dispatch<React.SetStateAction<boolean>>,
  getDetailData: () => Promise<void>,
  userName: string | null
}

interface GenerateRenderItemsArgument {
  applyId: string,
  currentlyLockedBy: string | null,
  currentUserId: string | null,
  isSupervisor: boolean | null,
}

interface RenderItems {
  alertMesssage: string | null;
  isdisabled: boolean;
  buttonText: string;
  onClickCallback: null | (() => Promise<void>)
}


const LockInformation = (props: Props) => {
  const {
    applyId,
    lockedBy,
    isBasicInformationInputChanged,
    setIsBasicInformationInputChanged,
    getDetailData,
    userName
  } = props;
  const [currentlyLockedBy, setCurrentlyLockedBy] = useState<string | null>(lockedBy);

  const lockHandler = async (
    applyId: string,
    isBasicInformationInputChanged: boolean,
    setIsBasicInformationInputChanged: React.Dispatch<React.SetStateAction<boolean>>) => {
    if (isBasicInformationInputChanged) {
      if (!window.confirm('ロックするとこれまでの変更が破棄されます。よろしいですか？')) return;
    }
    await lockApply(applyId)
      .then(() => {
        setCurrentlyLockedBy(currentUserId)
        getDetailData();
        setIsBasicInformationInputChanged(false)
      })
      .catch(err => {
        const statusCode: number | null = err.response ? err.response.status : null;
        if (statusCode === 400) {
          alert('すでにロックされています。ページを更新してください。')
        } else {
          alert('ロックに失敗しました。')
        }
      })
  }

  const unlockHandler = async (
    applyId: string,
    isBasicInformationInputChanged: boolean,
    setIsBasicInformationInputChanged: React.Dispatch<React.SetStateAction<boolean>>) => {
    if (isBasicInformationInputChanged) {
      if (!window.confirm('ロック解除するとこれまでの変更が破棄されます。よろしいですか？')) return;
    }
    await unlockApply(applyId)
      .then(() => {
        setCurrentlyLockedBy(null)
        getDetailData();
        setIsBasicInformationInputChanged(false)
      })
      .catch(err => {
        const statusCode: number | null = err.response ? err.response.status : null;
        if (statusCode === 400) {
          alert('すでにロック解除されています。ページを更新してください。')
        } else {
          alert('ロック解除に失敗しました。')
        }
      })
  }

  const generateRenderItems = (object: GenerateRenderItemsArgument): RenderItems => {
    const { applyId, currentlyLockedBy, currentUserId, isSupervisor } = object;
    const callbackWhenUnlockIsClicked = async () => await unlockHandler(
      applyId, isBasicInformationInputChanged, setIsBasicInformationInputChanged)

    if (currentlyLockedBy === null) {
      // ①画面が誰にもロックされていない
      return {
        alertMesssage: null,
        isdisabled: false,
        buttonText: 'ロック',
        onClickCallback: async () => await lockHandler(
          applyId, isBasicInformationInputChanged, setIsBasicInformationInputChanged)
      };
    } else if (currentlyLockedBy !== currentUserId && !isSupervisor) {
      // ②自分以外のユーザがロックしている && (SV or 社員権限)ではない
      return {
        alertMesssage: `他のユーザがロック中です。変更はできません。`,
        isdisabled: true,
        buttonText: 'ロック中',
        onClickCallback: null
      };
    } else if (currentlyLockedBy !== currentUserId && isSupervisor) {
      // ③自分以外のユーザがロックしている && (SV or 社員権限)
      return {
        alertMesssage: `${userName}がロックしています。`,
        isdisabled: false,
        buttonText: 'ロック解除',
        onClickCallback: callbackWhenUnlockIsClicked
      };
    } else if (currentlyLockedBy === currentUserId) {
      // ④自分がロックしている
      return {
        alertMesssage: 'ロック中です。他のユーザーは変更できません。',
        isdisabled: false,
        buttonText: 'ロック解除',
        onClickCallback: callbackWhenUnlockIsClicked
      };
    }
    throw new Error('引数が不正です。');
  }

  const currentUser = useSelector(currentUserSelector);
  const currentUserId = currentUser !== null ? currentUser.userId : null;
  const isSupervisor = currentUser !== null ? currentUser.groupName.includes('supervisor') : null;
  const { alertMesssage, isdisabled, buttonText, onClickCallback } = generateRenderItems({ applyId, currentlyLockedBy, currentUserId, isSupervisor })

  return (
    <div className={styles.lockInformation} >
      <div className={styles.lockButton}>
        <div className="alertArea">
          {alertMesssage !== null &&
            <Alert severity="warning">{alertMesssage}</Alert>
          }
        </div>
        <AsyncButton
          type="button"
          variant="contained"
          color="primary"
          onClick={onClickCallback}
          disabled={isdisabled}
        >
          {buttonText}
        </AsyncButton>
      </div>
    </div>
  )
};

export default LockInformation;
